import type { ComponentType } from 'react';
import type { Dispatch } from 'redux';
import { createSelector } from 'reselect';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { AnalyticsSubject } from '@atlassian/jira-analytics-web-react/src/components/decorators.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { connect } from '@atlassian/jira-react-redux';
import type { AccountId } from '@atlassian/jira-shared-types/src/general.tsx';
import type { AssigneeType } from '../../model/types';
import type { Action } from '../../state/actions';
import {
	getBaseUrlSelector,
	getProjectIdSelector,
	getProjectTypeSelector,
	getLocale,
	getIsAdmin,
	getProjectTypeMessageSelector,
} from '../../state/context/selectors';
import { getAllProjectCategories } from '../../state/entities/selectors';
import type { AppState } from '../../state/types';
import {
	projectKeyChange,
	projectNameChange,
	projectDescriptionChange,
	setProjectCategoryId,
	setProjectLeadAccountId,
	assigneeTypeChange,
	saveButtonClicked,
	fieldValidationForced,
	projectUrlChange,
	projectTeamTypeChange,
	projectTeamTypeFreeTextChange,
	onReleaseProjectKey,
	undoReleaseHistoricalKeysClicked,
} from '../../state/ui/actions';
import {
	isSaveButtonEnabledSelector,
	projectKeyUiSelector,
	projectNameUiSelector,
	validationErrorsSelector,
	getProjectCategoryToDisplay,
	forceValidationForSelector,
	projectLeadAccountIdUiSelector,
	projectDescriptionUiSelector,
	assigneeTypeUiSelector,
	allowUnassigned,
	projectUrlUiSelector,
	getSimplifiedSelector,
	projectTeamTypeUiSelector,
	projectTeamTypeFreeTextUiSelector,
	historicalProjectKeysSelector,
	releasedProjectKeysSelector,
} from '../../state/ui/selectors';
import Avatar from './avatar';
import FormInput from './form-input';
import { parseToADF, parseToWikiMarkup } from './utils';
import Form, { type Props } from './view';

const projectDescriptionMemoized = () =>
	createSelector(
		(state: AppState) => parseToADF(projectDescriptionUiSelector(state)),
		(res) => res,
	);
const mapStateToPropsFactory = () => {
	const projectDescriptionInstance = projectDescriptionMemoized();
	return (state: AppState) => ({
		isAdmin: getIsAdmin(state),
		projectName: projectNameUiSelector(state),
		projectKey: projectKeyUiSelector(state),
		historicalProjectKeys: fg('hix-4189-releasing-historical-project-keys')
			? historicalProjectKeysSelector(state)
			: [],
		releasedHistoricalKeys: fg('hix-4189-releasing-historical-project-keys')
			? releasedProjectKeysSelector(state)
			: [],
		projectDescription: !getSimplifiedSelector(state)
			? projectDescriptionInstance(state)
			: undefined,
		isSimplified: getSimplifiedSelector(state),
		projectUrl: !getSimplifiedSelector(state) ? projectUrlUiSelector(state) : undefined,
		projectLeadAccountId: projectLeadAccountIdUiSelector(state),
		projectType: getProjectTypeSelector(state),
		projectTypeMessage: !getSimplifiedSelector(state)
			? getProjectTypeMessageSelector(state)
			: undefined,
		assigneeType: assigneeTypeUiSelector(state),
		allowUnassigned: allowUnassigned(state),
		saveEnabled: isSaveButtonEnabledSelector(state),
		baseUrl: getBaseUrlSelector(state),
		projectId: getProjectIdSelector(state),
		locale: getLocale(state),
		validationErrors: validationErrorsSelector(state),
		Avatar,
		FormInput,
		allProjectCategories: getAllProjectCategories(state),
		selectedProjectCategory: getProjectCategoryToDisplay(state),
		forceValidationFor: forceValidationForSelector(state),
		projectTeamType: projectTeamTypeUiSelector(state),
		projectTeamTypeFreeText: projectTeamTypeFreeTextUiSelector(state),
	});
};

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
	onProjectNameChange(value: string) {
		dispatch(projectNameChange(value));
	},
	onProjectKeyChange(value: string) {
		dispatch(projectKeyChange(value));
	},
	onReleaseProjectKey(key: string, isSelected: boolean) {
		dispatch(onReleaseProjectKey(key, isSelected));
	},
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onProjectDescriptionChange(value: any) {
		dispatch(projectDescriptionChange(parseToWikiMarkup(value)));
	},
	onProjectUrlChange(value: string) {
		dispatch(projectUrlChange(value));
	},
	onSetProjectCategoryId(value: string) {
		dispatch(setProjectCategoryId(value));
	},
	onSetProjectLeadAccountId(value: AccountId) {
		dispatch(setProjectLeadAccountId(value));
	},
	onAssigneeTypeChange(assigneeType: AssigneeType) {
		dispatch(assigneeTypeChange(assigneeType));
	},
	onSaveButtonClicked(analyticsEvent: UIAnalyticsEvent) {
		dispatch(saveButtonClicked(analyticsEvent));
	},
	onFieldBlur: () => {
		dispatch(fieldValidationForced());
	},
	onUndoReleasedProjectKeys: () => {
		dispatch(undoReleaseHistoricalKeysClicked());
	},
	onProjectTeamTypeChange: (projectTeamType: string) => {
		dispatch(projectTeamTypeChange(projectTeamType));
	},
	onProjectTeamTypeFreeTextChange: (
		projectTeamTypeFreeText: string | undefined,
		error: boolean,
	) => {
		dispatch(projectTeamTypeFreeTextChange(projectTeamTypeFreeText, error));
	},
});

const EditProjectSubject: ComponentType<Props> = AnalyticsSubject('project')(Form);

export default connect(
	mapStateToPropsFactory,
	mapDispatchToProps,
	// @ts-expect-error - TS2345 - Argument of type 'ComponentType<Props>' is not assignable to parameter of type 'Component<{ isAdmin: boolean; projectName: string; projectKey: string; projectLeadAccountId: string; projectType: ProjectType; assigneeType: AssigneeType; allowUnassigned: boolean | undefined; ... 9 more ...; forceValidationFor: string | undefined; } & { ...; }>'.
)(EditProjectSubject);
